import React from 'react';
import { Eye, FileText, ImageIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/common/utils';
import { PasseriniAPI } from '@/services/apiService';
import { DniPreviewDialog } from './DniPreviewDialog';
import type {
  DniDocumentoRecord,
  DniDocumentoTipo,
  DniDocumentsResponse,
  OtorganteOption,
  ResponsableOption,
} from '../types';

interface PersonasListSectionProps {
  title: string;
  personas: (OtorganteOption | ResponsableOption)[];
  personaType?: 'otorgante' | 'responsable';
  dniDocuments?: DniDocumentsResponse;
  isLoadingDniDocuments?: boolean;
  dniDocumentsError?: boolean;
}

interface SelectedDniPreview {
  documento: DniDocumentoRecord;
  title: string;
}

const IMAGE_MIME_PREFIX = 'image/';

export const PersonasListSection: React.FC<PersonasListSectionProps> = ({
  title,
  personas,
  personaType,
  dniDocuments,
  isLoadingDniDocuments = false,
  dniDocumentsError = false,
}) => {
  const [selectedPreview, setSelectedPreview] = React.useState<SelectedDniPreview | null>(null);

  return (
    <>
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
        <ul className="space-y-2 text-sm text-gray-600 max-h-[400px] overflow-y-auto pr-1">
          {personas.map((persona) => {
            const personaDni = personaType ? dniDocuments?.[personaType]?.[persona.id] : undefined;
            const documentoEntries = (
              [
                ['frente', personaDni?.frente],
                ['dorso', personaDni?.dorso],
              ] as const
            ).filter((entry): entry is [DniDocumentoTipo, DniDocumentoRecord] => Boolean(entry[1]));

            return (
              <li key={persona.id} className="rounded-md border border-gray-100 bg-gray-50 p-3">
                <p className="break-words font-medium text-gray-900">{persona.nombre}</p>
                <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs text-gray-600">
                  <p>DNI: {persona.profile?.dni || persona.dni || 'Sin registrar'}</p>
                  <p>Tel: {persona.profile?.telefono || persona.telefono || 'Sin registrar'}</p>
                  <p className="col-span-2 break-all">Email: {persona.email || 'Sin registrar'}</p>
                </div>

                {personaType && (
                  <div className="mt-3">
                    {isLoadingDniDocuments && !dniDocuments && (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Skeleton className="h-28 w-full rounded-lg" />
                        <Skeleton className="h-28 w-full rounded-lg" />
                      </div>
                    )}

                    {!isLoadingDniDocuments && dniDocumentsError && !documentoEntries.length && (
                      <p className="text-xs text-red-600">
                        No se pudieron cargar las miniaturas del DNI.
                      </p>
                    )}

                    {!isLoadingDniDocuments && !documentoEntries.length && !dniDocumentsError && (
                      <p className="text-xs text-gray-500">Sin imágenes de DNI adjuntas.</p>
                    )}

                    {documentoEntries.length > 0 && (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {documentoEntries.map(([lado, documento]) => (
                          <DniThumbnailCard
                            key={documento.id}
                            documento={documento}
                            lado={lado}
                            personaNombre={persona.nombre}
                            onOpenPreview={(preview) => setSelectedPreview(preview)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <DniPreviewDialog
        documento={selectedPreview?.documento ?? null}
        open={Boolean(selectedPreview)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPreview(null);
          }
        }}
        title={selectedPreview?.title ?? 'Previsualización de DNI'}
      />
    </>
  );
};

interface DniThumbnailCardProps {
  documento: DniDocumentoRecord;
  lado: DniDocumentoTipo;
  personaNombre: string;
  onOpenPreview: (preview: SelectedDniPreview) => void;
}

const DniThumbnailCard: React.FC<DniThumbnailCardProps> = ({
  documento,
  lado,
  personaNombre,
  onOpenPreview,
}) => {
  const { previewUrl, isLoading, hasPreviewError, isImage } = useDniThumbnail(documento);
  const sideLabel = lado === 'frente' ? 'Frente' : 'Dorso';
  const previewTitle = `${personaNombre} · DNI ${sideLabel}`;

  return (
    <button
      type="button"
      onClick={() => onOpenPreview({ documento, title: previewTitle })}
      className={cn(
        'group overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition hover:border-gray-300 hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2'
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">DNI {sideLabel}</p>
          <p className="text-[11px] text-gray-400">{documento.formattedSize}</p>
        </div>
        <Eye className="h-4 w-4 text-gray-400 transition group-hover:text-gray-700" />
      </div>

      <div className="relative flex h-28 items-center justify-center bg-gray-100">
        {isLoading && <Skeleton className="h-full w-full rounded-none" />}

        {!isLoading && isImage && previewUrl && (
          <img
            src={previewUrl}
            alt={`Miniatura de DNI ${sideLabel} de ${personaNombre}`}
            className="h-full w-full object-cover"
          />
        )}

        {!isLoading && (!isImage || !previewUrl || hasPreviewError) && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-3 text-center">
            {documento.mimeType === 'application/pdf' ? (
              <FileText className="h-8 w-8 text-gray-500" />
            ) : (
              <ImageIcon className="h-8 w-8 text-gray-500" />
            )}
            <p className="line-clamp-2 text-xs text-gray-600">{documento.fileName}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-3 py-2">
        <span className="truncate text-xs text-gray-500">{documento.fileName}</span>
        <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition group-hover:bg-gray-200">
          Ver
        </span>
      </div>
    </button>
  );
};

const useDniThumbnail = (documento: DniDocumentoRecord) => {
  const isImage = documento.mimeType.startsWith(IMAGE_MIME_PREFIX);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(isImage);
  const [hasPreviewError, setHasPreviewError] = React.useState(false);

  React.useEffect(() => {
    if (!isImage || !documento.fileUrl) {
      setPreviewUrl(null);
      setIsLoading(false);
      setHasPreviewError(false);
      return;
    }

    let isMounted = true;
    let currentObjectUrl: string | null = null;

    const loadThumbnail = async () => {
      setIsLoading(true);
      setHasPreviewError(false);

      try {
        const response = await PasseriniAPI.get(documento.fileUrl.replace(/^\/api/, ''), {
          responseType: 'blob',
        });

        if (!isMounted) return;

        currentObjectUrl = URL.createObjectURL(response.data);
        setPreviewUrl(currentObjectUrl);
      } catch (error) {
        console.error('Error fetching DNI thumbnail:', error);
        if (isMounted) {
          setPreviewUrl(null);
          setHasPreviewError(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadThumbnail();

    return () => {
      isMounted = false;
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
      }
    };
  }, [documento.fileUrl, isImage]);

  return {
    previewUrl,
    isLoading,
    hasPreviewError,
    isImage,
  };
};
